import { useEffect, useState } from "react";
import Layout from "../layouts/layout";
import Loader from "../components/loader";
import PRODUCTS from "../api/services/PRODUCTS";
import CreateProductModal from "../components/createProductModal";
import { toast } from "react-toastify";
import { BASE_URL } from "../config";
import styles from "../styles/_screens/products.module.scss";

const Products = () => {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [responseStatus, setResponseStatus] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  // FETCH PRODUCTS
  useEffect(() => {
    setResponseStatus(null);
    const fetchData = async () => {
      try {
        const res = await PRODUCTS.get_all();
        // Backend returns { data: [...] } or { products: [...] } or plain array
        const products = res?.data ?? res?.products ?? res ?? [];
        setData(Array.isArray(products) ? products : []);
      } catch (error) {
        console.log(error);
        setData([]);
      }
    };
    fetchData();
  }, [responseStatus]);

  const handleDelete = async (id) => {
    if (!window.confirm("האם למחוק את המוצר?")) return;
    try {
      const res = await PRODUCTS.delete(id);
      toast.success(res?.data?.message || "המוצר נמחק בהצלחה");
      setResponseStatus(Date.now());
    } catch (error) {
      console.log(error);
      toast.error(error?.response?.data?.message || "שגיאה במחיקת המוצר");
    }
  };

  const openEdit = (product) => {
    setEditingProduct(product);
    setIsModalOpen(true);
  };

  const openCreate = () => {
    setEditingProduct(null);
    setIsModalOpen(true);
  };

  const filteredData = data
    ? data.filter((p) => {
        const q = search.toLowerCase();
        return (
          !q ||
          p.name?.toLowerCase().includes(q) ||
          p.description?.toLowerCase().includes(q) ||
          p.label?.toLowerCase().includes(q)
        );
      })
    : [];

  if (!data) {
    return (
      <Layout>
        <div className={styles.container}>
          <div className={styles.loaderWrap}>
            <Loader />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={styles.container} dir="rtl">
        {/* Header */}
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>מוצרים</h1>
          <button className={styles.createBtn} onClick={openCreate}>
            מוצר חדש +
          </button>
        </div>

        {/* Search */}
        <div className={styles.searchRow}>
          <input
            className={styles.searchInput}
            placeholder="חיפוש מוצר..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            dir="rtl"
          />
        </div>

        {/* Table / Empty */}
        {filteredData.length === 0 ? (
          <div className={styles.emptyState}>
            <p className={styles.emptyIcon}>📦</p>
            <p className={styles.emptyText}>
              {search ? "לא נמצאו מוצרים התואמים לחיפוש" : "אין מוצרים עדיין"}
            </p>
          </div>
        ) : (
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>תמונה</th>
                  <th>שם</th>
                  <th>תיאור</th>
                  <th>מחיר</th>
                  <th>מחיר מבצע</th>
                  <th>סטטוס</th>
                  <th>פעולות</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((product) => {
                  const imgSrc = product.image
                    ? `${BASE_URL}/uploads/${product.image}`
                    : null;

                  return (
                    <tr key={product._id}>
                      {/* Image */}
                      <td>
                        {imgSrc ? (
                          <img
                            src={imgSrc}
                            alt={product.name}
                            className={styles.productThumb}
                          />
                        ) : (
                          <div className={styles.productThumbPlaceholder}>📦</div>
                        )}
                      </td>

                      {/* Name */}
                      <td className={styles.nameCell}>{product.name}</td>

                      {/* Description */}
                      <td className={styles.descCell}>
                        {product.description || "—"}
                      </td>

                      {/* Price */}
                      <td className={styles.priceCell}>
                        {product.price != null ? `₪${product.price}` : "—"}
                      </td>

                      {/* Sale Price */}
                      <td>
                        {product.salePrice != null ? (
                          <span className={styles.salePriceCell}>
                            ₪{product.salePrice}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>

                      {/* Status badges */}
                      <td>
                        <div className={styles.badgeWrap}>
                          <span
                            className={`${styles.badge} ${
                              product.active ? styles.badgeActive : styles.badgeInactive
                            }`}
                          >
                            {product.active ? "פעיל" : "לא פעיל"}
                          </span>
                          {product.purchasable && (
                            <span className={`${styles.badge} ${styles.badgePurchasable}`}>
                              ניתן לרכישה
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Actions */}
                      <td>
                        <div className={styles.rowActions}>
                          <button
                            className={`${styles.rowBtn} ${styles.editBtn}`}
                            onClick={() => openEdit(product)}
                          >
                            ערוך
                          </button>
                          <button
                            className={`${styles.rowBtn} ${styles.deleteBtn}`}
                            onClick={() => handleDelete(product._id)}
                          >
                            מחק
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Modal */}
        {isModalOpen && (
          <CreateProductModal
            setIsOpen={setIsModalOpen}
            setResponseStatus={setResponseStatus}
            existingProduct={editingProduct}
          />
        )}
      </div>
    </Layout>
  );
};

export default Products;
